/**
 * DeckSwitcher — global keyboard-triggered deck picker.
 * Open with 'g' (when not in an input). Close with Escape or backdrop click.
 * Navigate results with ↑↓, select with Enter or click.
 */

import { useEffect, useRef, useState, useMemo, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { decks } from '@/lib/api';
import type { Deck } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { BRACKET_COLORS } from '@/lib/constants';

/** Render a small "⌖ variant" label for materialized-variant decks. */
function VariantChip() {
  return (
    <span
      className="text-xs font-semibold px-[6px] py-[1px] rounded-pill flex-shrink-0"
      style={{
        background: 'var(--surface-container)',
        color: 'var(--on-surface-variant)',
        border: '1px solid var(--outline-variant)',
      }}
    >
      ⌖ variant
    </span>
  );
}

interface DeckSwitcherProps {
  open: boolean;
  onClose: () => void;
}

export function DeckSwitcher({ open, onClose }: DeckSwitcherProps) {
  const [query, setQuery] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data } = useQuery({
    queryKey: ['decks', 'my'],
    queryFn: () => decks.listMy(),
    enabled: !!user,
    staleTime: 30_000,
  });

  const allDecks = data ?? [];

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return allDecks.slice(0, 10);

    // Rank so that deck name / commander matches outrank cards-list matches,
    // and prefix matches outrank substring matches. Lower score = better.
    //   0 — deck name starts with query
    //   1 — deck name contains query
    //   2 — commander name starts with query
    //   3 — commander name contains query
    //   4 — some card in the decklist contains query (catch-all, least relevant)
    //   -  — no match, filtered out
    const scored: Array<{ deck: Deck; score: number }> = [];
    for (const d of allDecks) {
      const name = (d.name ?? '').toLowerCase();
      const commanders = (d.commanders ?? []).map(c => c.toLowerCase());
      let score = -1;
      if (name.startsWith(q))               score = 0;
      else if (name.includes(q))            score = 1;
      else if (commanders.some(c => c.startsWith(q))) score = 2;
      else if (commanders.some(c => c.includes(q)))   score = 3;
      else if (Object.keys(d.cards ?? {}).some(n => n.toLowerCase().includes(q))) score = 4;
      if (score >= 0) scored.push({ deck: d, score });
    }
    // Stable sort by score ascending; ties broken by deck name.
    scored.sort((a, b) =>
      a.score - b.score ||
      (a.deck.name ?? '').localeCompare(b.deck.name ?? '')
    );
    return scored.slice(0, 10).map(s => s.deck);
  }, [allDecks, query]);

  // Auto-focus and reset state when opened
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIdx(0);
      // Small delay to let React render before focusing
      const t = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Reset highlight when results change
  useEffect(() => {
    setSelectedIdx(0);
  }, [filtered.length]);

  // Scroll selected item into view when navigating with keyboard
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const item = list.children[selectedIdx] as HTMLElement | undefined;
    item?.scrollIntoView({ block: 'nearest' });
  }, [selectedIdx]);

  function handleSelect(deck: Deck) {
    navigate(`/deck/${deck.id}`);
    onClose();
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (filtered[selectedIdx]) handleSelect(filtered[selectedIdx]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-start justify-center"
      style={{ paddingTop: '18vh', background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-[500px] rounded-xl overflow-hidden shadow-2xl"
        style={{ background: 'var(--surface-container-high)', border: '1px solid var(--outline-variant)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Search row */}
        <div className="flex items-center gap-2.5 px-4 py-3 border-b border-outline-variant">
          <span className="material-icons text-on-surface-variant" style={{ fontSize: 20 }}>search</span>
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Jump to deck…"
            className="flex-1 bg-transparent outline-none text-on-surface placeholder-on-surface-variant text-base"
          />
          <kbd
            className="text-xs text-on-surface-variant px-1.5 py-0.5 rounded"
            style={{ background: 'var(--surface-container)', border: '1px solid var(--outline-variant)', fontFamily: 'monospace' }}
          >
            esc
          </kbd>
        </div>

        {/* Results list */}
        {filtered.length > 0 ? (
          <ul ref={listRef} className="max-h-80 overflow-y-auto py-1">
            {filtered.map((deck, i) => {
              const commanders = deck.commanders ?? [];
              const bracket = deck.latest_analysis?.power_level?.wotc_bracket;
              const isSelected = i === selectedIdx;
              const isVariant = deck.source_type === 'variant_materialized';
              return (
                <li
                  key={deck.id}
                  onClick={() => handleSelect(deck)}
                  onMouseEnter={() => setSelectedIdx(i)}
                  className="flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors duration-75"
                  style={{ background: isSelected ? 'var(--surface-container-highest)' : 'transparent' }}
                >
                  <span className="material-icons text-on-surface-variant flex-shrink-0" style={{ fontSize: 16 }}>
                    style
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-on-surface truncate">{deck.name}</div>
                    {commanders.length > 0 && (
                      <div className="text-xs text-on-surface-variant truncate">{commanders.join(' / ')}</div>
                    )}
                  </div>
                  {isVariant && <VariantChip />}
                  {bracket != null && (
                    <span
                      className="text-xs font-bold flex-shrink-0"
                      style={{ color: BRACKET_COLORS[bracket] }}
                    >
                      B{bracket}
                    </span>
                  )}
                  {isSelected && (
                    <span className="material-icons text-on-surface-variant flex-shrink-0" style={{ fontSize: 14 }}>
                      keyboard_return
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="px-4 py-8 text-center text-sm text-on-surface-variant">
            {user ? 'No decks match' : 'Log in to use deck switcher'}
          </div>
        )}

        {/* Footer hints */}
        <div
          className="flex gap-4 px-4 py-1.5 border-t border-outline-variant text-xs text-on-surface-variant"
          style={{ background: 'var(--surface-container)' }}
        >
          <span><kbd style={{ fontFamily: 'monospace' }}>↑↓</kbd> navigate</span>
          <span><kbd style={{ fontFamily: 'monospace' }}>↵</kbd> open</span>
          <span><kbd style={{ fontFamily: 'monospace' }}>esc</kbd> close</span>
          <span className="ml-auto opacity-50">press <kbd style={{ fontFamily: 'monospace' }}>g</kbd> anytime</span>
        </div>
      </div>
    </div>
  );
}
